<template>
  <div class="loan-details-section-wrapper" v-show="!loading">
    <div class="loan-details-section" ref="schoolConfirmationRef" id="schoolConfirmation">
      <div class="loan-details-section-header">
        School Confirmation
        <a
          class="edit-loan-config-link"
          href="#/"
          @click.prevent="openSchoolConfirmationModal"
          :disabled="loading"
        >
          Edit
        </a>
      </div>

      <div class="loan-details-section-content two-column-grid">
        <div class="info-section" id="school">
          <p class="info-title">School</p>
          <div class="info">
            <p>{{ schoolName }}</p>
          </div>
        </div>

        <div class="info-section" id="graduationDate">
          <p class="info-title">Graduation Date<span class="error">*</span></p>
          <div class="info">
            {{ graduationDate }}
          </div>
        </div>

        <div class="info-section" id="academicTerms">
          <p class="info-title">Academic Term(s)</p>
          <div class="info">
            {{ academicTerms }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <div
    class="loan-details-section"
    ref="loanTermsRef"
    id="interestRateAndLoanTerms"
    v-show="!loading"
  >
    <div class="loan-details-section-header">
      Interest Rate & Line of Credit Terms
      <a
        class="edit-loan-config-link"
        href="#/"
        @click.prevent="openLoanConfigurationModal"
        :disabled="loading"
      >
        Edit
      </a>
    </div>
    <div class="loan-details-section-content single-row">
      <div class="info-section" id="maxApprovedAmount">
        <div class="info-title">Maximum Approved Line of Credit Amount</div>
        <div class="info">{{ locDetails?.maxApprovedAmountDisplay }}</div>
      </div>
    </div>
    <div class="loan-details-section-content two-column-grid">
      <div class="info-section" id="loanAmount">
        <div class="info-title" :class="{ disabled: schoolUndecided }">
          Current Requested Amount&nbsp;<tooltip-component
            ref="loanAmountTooltipRef"
            class="xs-bottom-center xs-bottom-center-right-auto"
            v-if="schoolUndecided"
            ><template #toggle>&nbsp;<icon icon="info" /></template>
            <div>
              This value cannot be edited until after you provide a school, graduation date, and
              academic term.
            </div></tooltip-component
          >
        </div>
        <div class="info">
          <p>{{ loanAmount }}</p>
        </div>
      </div>

      <div class="info-section" id="loanAmountPerTerm">
        <p class="info-title">Amount Per Academic Term</p>
        <div class="info">
          {{ loanAmountPerTerm }}
        </div>
      </div>

      <div class="info-section" id="repaymentType">
        <p class="info-title">Repayment Type<span class="error">*</span></p>
        <div class="info">
          {{ locDetails?.repaymentTypeDisplay }}
        </div>
      </div>

      <div class="info-section" id="rateSelection">
        <p class="info-title">Rate Selection<span class="error">*</span></p>
        <div class="info">
          {{ locDetails?.rateSelectionDisplay }}
        </div>
      </div>

      <div v-if="showPaymentEstimates" class="info-section" id="inSchoolPayments">
        <p class="info-title">
          {{ locDetails?.monthlyInSchoolPaymentLabel || "In-School" }} Monthly Est. Payments
        </p>
        <div class="info">
          {{ locDetails?.monthlyInSchoolPaymentsDisplay }}
        </div>
      </div>

      <div v-if="showPaymentEstimates" class="info-section" id="postGradPayments">
        <p class="info-title">
          {{ locDetails?.monthlyPostSchoolPaymentLabel || "Post-Grad" }} Monthly Est. Payments
        </p>
        <div class="info">
          {{ locDetails?.monthlyPostGradPaymentsDisplay }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { PelLoanConfigurationDetails } from "@/models/pel";
import TooltipComponent from "@/components/shared/TooltipComponent.vue";
import Icon from "@/components/shared/Icon.vue";
import useApplicationData from "@/composables/useApplicationData";

const props = defineProps({
  loading: { type: Boolean, required: true },
});

const store = useStore();

const emit = defineEmits<{
  (e: "complete", payload: boolean): void;
  (e: "close", payload: boolean): void;
  (e: "cancel", payload: boolean): void;
  (e: "openSchoolConfirmationModal", payload: boolean): void;
  (e: "openLoanOptionsModal", payload: boolean): void;
  (e: "openLoanConfigurationModal", payload: boolean): void;
}>();

const { isValidProfessionalGradFlow } = useApplicationData();

const locDetails = computed<PelLoanConfigurationDetails>(
  () => store.state.loanConfigurationModule.applicationInfo,
);

const showPaymentEstimates = computed<boolean>(() => isValidProfessionalGradFlow.value);

const loanAmount = computed<string>(
  () => locDetails.value?.loanAmountDisplay || "Not Yet Selected",
);
const loanAmountPerTerm = computed<string>(
  () => locDetails.value?.loanAmountPerTermDisplay || "Not Yet Selected",
);
const schoolName = computed<string>(() => locDetails.value?.schoolName || "Not Yet Selected");
const graduationDate = computed<string>(
  () => locDetails.value?.graduationDateDisplay || "Not Yet Selected",
);
const academicTerms = computed<string>(() => locDetails.value?.academicTerms || "Not Yet Selected");
const schoolUndecided = computed<boolean>(() => locDetails.value?.schoolUndecided);

// Methods
function openSchoolConfirmationModal() {
  emit("openSchoolConfirmationModal", true);
}

function openLoanOptionsModal() {
  emit("openLoanOptionsModal", true);
}

function openLoanConfigurationModal() {
  emit("openLoanConfigurationModal", true);
}
</script>

<style scoped>
* {
  font-family: var(--header-font);
}

p {
  margin: 0;
  padding: 0;
}

.loan-details-section-wrapper {
  display: flex;
}

.loan-details-section-wrapper .loan-details-section {
  display: flex;
  flex-direction: column;
  margin-bottom: 23px;
  width: 100%;
}

.loan-details-section .loan-details-section-header {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: var(--font-weight-bold);
  font-size: 1.25rem;
  margin-bottom: 7px;
}

.loan-details-section .loan-details-section-header a.edit-loan-config-link {
  color: #000;
  font-family: var(--header-font);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: var(--font-weight-medium);
  line-height: 21px; /* 175% */
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: none;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
}

.loan-details-section .loan-details-section-header a.edit-loan-config-link[disabled="true"] {
  color: #c3c3c3;
  cursor: not-allowed;
}

.loan-details-section .loan-details-section-content {
  width: 100%;
}

.loan-details-section .loan-details-section-content .info-section .info-title {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-semibold);
}

.loan-details-section .loan-details-section-content .info-section .info {
  font-size: 0.875rem;
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 21px; /* 150% */
}

.loan-details-section .loan-details-section-content .info-section {
  margin-bottom: 23px;
}

.two-column-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
}

:nth-child(1 of .loan-details-section) {
  margin-bottom: 0px;
}

.info-section:is([id="academicTerms"], [id="inSchoolPayments"], [id="postGradPayments"]) {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-bottom: 0px;
}

.loan-details-section#interestRateAndLoanTerms {
  margin-bottom: 23px;
}

.error {
  color: #8b0e04;
}

@media (max-width: 576px) {
  .two-column-grid {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .loan-details-section .loan-details-section-content .info-section {
    margin-bottom: 14px;
  }
}
</style>
